import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CompareArrowsOutlined from "@mui/icons-material/CompareArrowsOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_COMPACT_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { useGetJobCardOpenVsClosedService } from "subModule/src/services/analytics/useAnalytics";
import { GetJobCardOpenVsClosedResponse } from "subModule/src/services/analytics/interface";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";

interface JobCardOpenVsClosedProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

const TRACK_COLOR = "#EEF1F4";
const CHART_SIZE = 118;
const STROKE = 9;

function RadialRing({
  value,
  max,
  radius,
  color,
  title,
}: {
  value: number;
  max: number;
  radius: number;
  color: string;
  title: string;
}) {
  const circumference = 2 * Math.PI * radius;
  const pct = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const dash = circumference * pct;

  return (
    <g>
      <circle
        cx={CHART_SIZE / 2}
        cy={CHART_SIZE / 2}
        r={radius}
        fill="none"
        stroke={TRACK_COLOR}
        strokeWidth={STROKE}
      />
      <circle
        cx={CHART_SIZE / 2}
        cy={CHART_SIZE / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform={`rotate(-90 ${CHART_SIZE / 2} ${CHART_SIZE / 2})`}
        style={{ transition: "stroke-dasharray 0.4s ease" }}
      >
        <title>{title}</title>
      </circle>
    </g>
  );
}

export default function JobCardOpenVsClosed(props: JobCardOpenVsClosedProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetJobCardOpenVsClosedService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 10);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetJobCardOpenVsClosedResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const openCount = Number(data?.open || 0);
  const closedCount = Number(data?.closed || 0);
  const total = openCount + closedCount;
  const openColor = props.themes?.primary || "#1CACDC";
  const closedColor = props.themes?.secondary || "#FC5F3A";
  const outerRadius = 48;
  const innerRadius = 34;

  const formatCount = (value: number) =>
    new Intl.NumberFormat("en", {
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 2,
    }).format(value);

  return (
    <Card
      sx={{
        height: DASHBOARD_COMPACT_WIDGET_HEIGHT,
        minHeight: DASHBOARD_COMPACT_WIDGET_HEIGHT,
        boxShadow: "none",
        borderRadius: "12px",
        border: "1px solid #F0F2F5",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "14px 16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0 }}>
          <AnalyticsWidgetHeader
            title={t("JobCardOpenVsClosed")}
            icon={<CompareArrowsOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "jcOpenClosed"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "jcOpenClosed",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "jcOpenClosed",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions("jcOpenClosed", "loadNext");
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ mt: 1.5, flex: 1 }}>
            <Skeleton
              variant="circular"
              width={100}
              height={100}
              sx={{
                backgroundColor: props?.themes?.secondary,
                mx: "auto",
                mb: 1,
              }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 2.5,
              minHeight: 0,
              pt: 0.5,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: CHART_SIZE,
                height: CHART_SIZE,
                flexShrink: 0,
              }}
            >
              <svg width={CHART_SIZE} height={CHART_SIZE}>
                <RadialRing
                  value={closedCount}
                  max={total || 1}
                  radius={outerRadius}
                  color={closedColor}
                  title={`${t("closed")}: ${closedCount.toLocaleString()}`}
                />
                <RadialRing
                  value={openCount}
                  max={total || 1}
                  radius={innerRadius}
                  color={openColor}
                  title={`${t("open")}: ${openCount.toLocaleString()}`}
                />
              </svg>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#919EAB",
                    lineHeight: 1,
                  }}
                >
                  {t("total")}
                </Typography>
                <Tooltip
                  title={total.toLocaleString()}
                  placement="top"
                  enterDelay={0}
                >
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 17,
                      fontWeight: 800,
                      color: "#212B36",
                      lineHeight: 1.2,
                      pointerEvents: "auto",
                    }}
                  >
                    {formatCount(total)}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                justifyContent: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: openColor,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  className="font-Saira"
                  sx={{ fontSize: 11, fontWeight: 500, color: "#637381" }}
                >
                  {t("open")}
                </Typography>
                <Tooltip
                  title={openCount.toLocaleString()}
                  placement="top"
                  enterDelay={0}
                >
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: openColor,
                      ml: 0.25,
                    }}
                  >
                    {openCount.toLocaleString()}
                  </Typography>
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: closedColor,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  className="font-Saira"
                  sx={{ fontSize: 11, fontWeight: 500, color: "#637381" }}
                >
                  {t("closed")}
                </Typography>
                <Tooltip
                  title={closedCount.toLocaleString()}
                  placement="top"
                  enterDelay={0}
                >
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: closedColor,
                      ml: 0.25,
                    }}
                  >
                    {closedCount.toLocaleString()}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
